import { useParams, useNavigate, Link } from "react-router-dom";
import { useAdminWebsite } from "../../hooks/use-admin.js";
import { PageSkeleton } from "../../components/skeleton.js";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "\u2014";
  return new Date(dateStr).toLocaleDateString("en-IE", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AdminWebsiteDetailPage() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useAdminWebsite(websiteId);

  if (isLoading) return <PageSkeleton />;
  if (!data)
    return <div className="alert alert--error">Website not found.</div>;

  return (
    <div className="product-page">
      <button
        type="button"
        className="btn btn--secondary btn--sm mb-3"
        onClick={() => navigate("/admin/websites")}
      >
        &larr; Back to websites
      </button>
      <h1 className="page-title">{data.domain}</h1>

      <section className="product-section">
        <h2 className="section-title">Details</h2>
        <div className="stat-grid">
          <div className="stat-item">
            <span className="stat-item__value">{data.url}</span>
            <span className="stat-item__label">URL</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.latestScore !== null ? `${data.latestScore}%` : "\u2014"}
            </span>
            <span className="stat-item__label">Latest Score</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.latestConfidence || "\u2014"}
            </span>
            <span className="stat-item__label">Confidence</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.verified ? "Yes" : "No"}
            </span>
            <span className="stat-item__label">Verified</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.documentScanEnabled ? "Yes" : "No"}
            </span>
            <span className="stat-item__label">Document Scanning</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.recurringEnabled ? "Yes" : "No"}
            </span>
            <span className="stat-item__label">Recurring Monitoring</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {formatDate(data.latestReportAt)}
            </span>
            <span className="stat-item__label">Latest Report</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {formatDate(data.lastSuccessfulRunAt)}
            </span>
            <span className="stat-item__label">Last Successful Run</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {formatDate(data.lastFailedRunAt)}
            </span>
            <span className="stat-item__label">Last Failed Run</span>
          </div>
        </div>
      </section>

      <section className="product-section">
        <h2 className="section-title">Links</h2>
        <ul>
          <li className="mb-2">
            Organisation:{" "}
            <Link to={`/admin/orgs/${data.orgId}`}>View org</Link>
          </li>
          <li className="mb-2">
            View as user:{" "}
            <Link to={`/websites/${data.id}`}>{data.domain}</Link>
          </li>
        </ul>
      </section>

      {data.warnings?.length > 0 && (
        <section className="product-section warning-border">
          <h2 className="section-title">Warnings</h2>
          {data.warnings.map((w, i) => (
            <p key={i} className="text-sm mb-2">
              {w}
            </p>
          ))}
        </section>
      )}
    </div>
  );
}
